// Shared types and mock data for the Document Management Screen

export interface Invoice {
  id: string
  vendor: string
  documentNo?: string
  documentType: string
  status: string
  team: string
  amount: string
  receivedDate: string
  documentDate: string
  dueDate?: string
  poNo?: string
  tag?: string
  timestamp: string
  lockedBy?: string
}

export interface InvoiceDocument {
  id: string
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  receivedDate?: string
  currency: string
  supplier: string
  supplierABN?: string
  poNumber?: string
  matchingType?: '2-way' | '3-way' | 'non-po'
  subtotal: number
  taxAmt: number
  total: number
  notes?: string
  attachments?: string[]
  lineItems: LineItem[]
  imageUrl?: string
}

export interface LineItem {
  id: string
  itemCode: string
  description: string
  quantity: number
  unitPrice: number
  subtotal: number
  taxCode: string
  taxAmt: number
  total: number
  type: string
  notes?: string
  glCode?: string
  poMatch?: any
  grMatch?: any
}

export interface InvoiceDetails {
  vendorName: string
  supplierCode: string
  vendorTaxNo: string
  email: string
  phoneNumber?: string
  vendorBankCheck?: boolean
  entity: string
  site: string
  address: string
  city: string
  state: string
  postcode: string
  country: string
  lastVerifiedDate?: string
  paymentTerms?: string
}

// Mock invoices for sidebar
export const mockInvoices: Invoice[] = [
  {
    id: '1',
    vendor: 'Acme Corp',
    documentNo: 'INV-001',
    documentType: 'PO Invoice',
    status: 'Pending Review',
    team: 'AP Team',
    amount: '$12,500.00',
    receivedDate: '2024-01-15',
    documentDate: '2024-01-10',
    dueDate: '2024-02-10',
    poNo: 'PO-2024-001',
    timestamp: '2 hours ago',
  },
  {
    id: '2',
    vendor: 'Global Supplies',
    documentNo: 'INV-002',
    documentType: 'Non-PO Invoice',
    status: 'In Progress',
    team: 'Sarah Chen',
    amount: '$3,200.50',
    receivedDate: '2024-01-14',
    documentDate: '2024-01-12',
    dueDate: '2024-02-12',
    timestamp: '5 hours ago',
  },
  {
    id: '3',
    vendor: 'Tech Solutions',
    documentNo: 'INV-003',
    documentType: 'PO Invoice',
    status: 'Pending Review',
    team: 'AP Team',
    amount: '$8,750.00',
    receivedDate: '2024-01-13',
    documentDate: '2024-01-08',
    dueDate: '2024-02-08',
    poNo: 'PO-2024-003',
    timestamp: '1 day ago',
  },
  {
    id: '4',
    vendor: 'Office Plus',
    documentNo: 'INV-004',
    documentType: 'Non-PO Invoice',
    status: 'Exception',
    team: 'Mike Johnson',
    amount: '$1,450.25',
    receivedDate: '2024-01-12',
    documentDate: '2024-01-05',
    dueDate: '2024-02-05',
    timestamp: '1 day ago',
  },
  {
    id: '5',
    vendor: 'BuildRight Ltd',
    documentNo: 'INV-005',
    documentType: 'PO Invoice',
    status: 'Approved',
    team: 'Emma Wilson',
    amount: '$45,000.00',
    receivedDate: '2024-01-11',
    documentDate: '2024-01-03',
    dueDate: '2024-02-03',
    poNo: 'PO-2024-005',
    timestamp: '2 days ago',
  },
  {
    id: '6',
    vendor: 'Acme Corp',
    documentNo: 'CN-001',
    documentType: 'Credit Note',
    status: 'Pending Review',
    team: 'AP Team',
    amount: '-$2,500.00',
    receivedDate: '2024-01-10',
    documentDate: '2024-01-08',
    timestamp: '3 days ago',
  },
]

// Mock document data
export const mockDocuments: Record<string, InvoiceDocument> = {
  '1': {
    id: '1',
    invoiceNumber: 'INV-001',
    invoiceDate: '10 Jan 2024',
    dueDate: '10 Feb 2024',
    receivedDate: '15 Jan 2024',
    currency: 'AUD',
    supplier: 'Acme Corp',
    supplierABN: '12 345 678 901',
    poNumber: 'PO-2024-001',
    matchingType: '3-way',
    subtotal: 11363.64,
    taxAmt: 1136.36,
    total: 12500.0,
    notes: 'Monthly supply order',
    imageUrl:
      'https://templates.invoicehome.com/invoice-template-us-neat-750px.png',
    lineItems: [
      {
        id: 'line-1-1',
        itemCode: 'WDG-001',
        description: 'Industrial Widgets',
        quantity: 500,
        unitPrice: 15.0,
        subtotal: 7500.0,
        taxCode: 'I',
        taxAmt: 750.0,
        total: 8250.0,
        type: 'Goods',
      },
      {
        id: 'line-1-2',
        itemCode: 'BLT-002',
        description: 'Steel Bolts M10',
        quantity: 1000,
        unitPrice: 3.86,
        subtotal: 3863.64,
        taxCode: 'I',
        taxAmt: 386.36,
        total: 4250.0,
        type: 'Goods',
      },
    ],
  },
  '2': {
    id: '2',
    invoiceNumber: 'INV-002',
    invoiceDate: '12 Jan 2024',
    dueDate: '12 Feb 2024',
    receivedDate: '14 Jan 2024',
    currency: 'AUD',
    supplier: 'Global Supplies',
    supplierABN: '98 765 432 109',
    matchingType: 'non-po',
    subtotal: 2909.55,
    taxAmt: 290.95,
    total: 3200.5,
    imageUrl:
      'https://templates.invoicehome.com/invoice-template-us-neat-750px.png',
    lineItems: [
      {
        id: 'line-2-1',
        itemCode: 'OFF-010',
        description: 'Office Furniture - Desk',
        quantity: 5,
        unitPrice: 450.0,
        subtotal: 2250.0,
        taxCode: 'I',
        taxAmt: 225.0,
        total: 2475.0,
        type: 'Goods',
      },
      {
        id: 'line-2-2',
        itemCode: 'OFF-011',
        description: 'Ergonomic Chair',
        quantity: 3,
        unitPrice: 219.85,
        subtotal: 659.55,
        taxCode: 'I',
        taxAmt: 65.95,
        total: 725.5,
        type: 'Goods',
      },
    ],
  },
  '3': {
    id: '3',
    invoiceNumber: 'INV-003',
    invoiceDate: '08 Jan 2024',
    dueDate: '08 Feb 2024',
    receivedDate: '13 Jan 2024',
    currency: 'AUD',
    supplier: 'Tech Solutions',
    supplierABN: '55 123 456 789',
    poNumber: 'PO-2024-003',
    matchingType: '2-way',
    subtotal: 7954.55,
    taxAmt: 795.45,
    total: 8750.0,
    imageUrl:
      'https://templates.invoicehome.com/invoice-template-us-neat-750px.png',
    lineItems: [
      {
        id: 'line-3-1',
        itemCode: 'SRV-001',
        description: 'IT Consulting Services',
        quantity: 40,
        unitPrice: 150.0,
        subtotal: 6000.0,
        taxCode: 'I',
        taxAmt: 600.0,
        total: 6600.0,
        type: 'Service',
      },
      {
        id: 'line-3-2',
        itemCode: 'LIC-001',
        description: 'Software License Annual',
        quantity: 10,
        unitPrice: 195.45,
        subtotal: 1954.55,
        taxCode: 'I',
        taxAmt: 195.45,
        total: 2150.0,
        type: 'Service',
      },
    ],
  },
  '4': {
    id: '4',
    invoiceNumber: 'INV-004',
    invoiceDate: '05 Jan 2024',
    dueDate: '05 Feb 2024',
    receivedDate: '12 Jan 2024',
    currency: 'AUD',
    supplier: 'Office Plus',
    supplierABN: '33 987 654 321',
    matchingType: 'non-po',
    subtotal: 1318.41,
    taxAmt: 131.84,
    total: 1450.25,
    notes: 'Stationery and supplies for Q1',
    imageUrl:
      'https://templates.invoicehome.com/invoice-template-us-neat-750px.png',
    lineItems: [
      {
        id: 'line-4-1',
        itemCode: 'STN-001',
        description: 'Printer Paper A4 (Box)',
        quantity: 20,
        unitPrice: 45.0,
        subtotal: 900.0,
        taxCode: 'I',
        taxAmt: 90.0,
        total: 990.0,
        type: 'Goods',
      },
      {
        id: 'line-4-2',
        itemCode: 'STN-002',
        description: 'Toner Cartridges',
        quantity: 5,
        unitPrice: 83.68,
        subtotal: 418.41,
        taxCode: 'I',
        taxAmt: 41.84,
        total: 460.25,
        type: 'Goods',
      },
    ],
  },
  '5': {
    id: '5',
    invoiceNumber: 'INV-005',
    invoiceDate: '03 Jan 2024',
    dueDate: '03 Feb 2024',
    receivedDate: '11 Jan 2024',
    currency: 'AUD',
    supplier: 'BuildRight Ltd',
    supplierABN: '77 456 789 012',
    poNumber: 'PO-2024-005',
    matchingType: '3-way',
    subtotal: 40909.09,
    taxAmt: 4090.91,
    total: 45000.0,
    imageUrl:
      'https://templates.invoicehome.com/invoice-template-us-neat-750px.png',
    lineItems: [
      {
        id: 'line-5-1',
        itemCode: 'CON-001',
        description: 'Concrete Supply - Grade 40',
        quantity: 50,
        unitPrice: 545.45,
        subtotal: 27272.73,
        taxCode: 'I',
        taxAmt: 2727.27,
        total: 30000.0,
        type: 'Goods',
      },
      {
        id: 'line-5-2',
        itemCode: 'CON-002',
        description: 'Steel Reinforcement',
        quantity: 20,
        unitPrice: 681.82,
        subtotal: 13636.36,
        taxCode: 'I',
        taxAmt: 1363.64,
        total: 15000.0,
        type: 'Goods',
      },
    ],
  },
  '6': {
    id: '6',
    invoiceNumber: 'CN-001',
    invoiceDate: '08 Jan 2024',
    dueDate: '08 Feb 2024',
    receivedDate: '10 Jan 2024',
    currency: 'AUD',
    supplier: 'Acme Corp',
    supplierABN: '12 345 678 901',
    matchingType: 'non-po',
    subtotal: -2272.73,
    taxAmt: -227.27,
    total: -2500.0,
    notes: 'Credit for returned widgets',
    imageUrl:
      'https://templates.invoicehome.com/invoice-template-us-neat-750px.png',
    lineItems: [
      {
        id: 'line-6-1',
        itemCode: 'WDG-001',
        description: 'Industrial Widgets (Returned)',
        quantity: -100,
        unitPrice: 22.73,
        subtotal: -2272.73,
        taxCode: 'I',
        taxAmt: -227.27,
        total: -2500.0,
        type: 'Credit',
      },
    ],
  },
}

export function getDocumentDetails(doc: InvoiceDocument): InvoiceDetails {
  const vendorDetails: Record<string, Partial<InvoiceDetails>> = {
    'Acme Corp': {
      supplierCode: 'ACM-001',
      vendorTaxNo: '12 345 678 901',
      email: 'accounts@acmecorp.com.au',
      phoneNumber: '+61 2 9876 5432',
      entity: 'Acme Corporation Pty Ltd',
      site: 'Sydney HQ',
      address: '100 George Street',
      city: 'Sydney',
      state: 'NSW',
      postcode: '2000',
      country: 'Australia',
      vendorBankCheck: true,
      lastVerifiedDate: '15 Dec 2023',
      paymentTerms: 'Net 30',
    },
    'Global Supplies': {
      supplierCode: 'GLS-002',
      vendorTaxNo: '98 765 432 109',
      email: 'billing@globalsupplies.com',
      phoneNumber: '+61 3 1234 5678',
      entity: 'Global Supplies Pty Ltd',
      site: 'Melbourne Office',
      address: '50 Collins Street',
      city: 'Melbourne',
      state: 'VIC',
      postcode: '3000',
      country: 'Australia',
      vendorBankCheck: true,
      lastVerifiedDate: '20 Dec 2023',
      paymentTerms: 'Net 14',
    },
    'Tech Solutions': {
      supplierCode: 'TEC-003',
      vendorTaxNo: '55 123 456 789',
      email: 'invoices@techsolutions.com.au',
      phoneNumber: '+61 7 9876 1234',
      entity: 'Tech Solutions Australia',
      site: 'Brisbane Office',
      address: '200 Queen Street',
      city: 'Brisbane',
      state: 'QLD',
      postcode: '4000',
      country: 'Australia',
      vendorBankCheck: true,
      lastVerifiedDate: '10 Jan 2024',
      paymentTerms: 'Net 30',
    },
    'Office Plus': {
      supplierCode: 'OFP-004',
      vendorTaxNo: '33 987 654 321',
      email: 'accounts@officeplus.com.au',
      entity: 'Office Plus Pty Ltd',
      site: 'Perth Warehouse',
      address: '75 St Georges Terrace',
      city: 'Perth',
      state: 'WA',
      postcode: '6000',
      country: 'Australia',
      vendorBankCheck: false,
      paymentTerms: 'Net 7',
    },
    'BuildRight Ltd': {
      supplierCode: 'BRL-005',
      vendorTaxNo: '77 456 789 012',
      email: 'finance@buildright.com.au',
      phoneNumber: '+61 2 5555 1234',
      entity: 'BuildRight Limited',
      site: 'Sydney Construction',
      address: '300 Pitt Street',
      city: 'Sydney',
      state: 'NSW',
      postcode: '2000',
      country: 'Australia',
      vendorBankCheck: true,
      lastVerifiedDate: '05 Jan 2024',
      paymentTerms: 'Net 45',
    },
  }
  const details = vendorDetails[doc.supplier] || {}
  return {
    vendorName: doc.supplier,
    supplierCode: details.supplierCode || 'N/A',
    vendorTaxNo: details.vendorTaxNo || doc.supplierABN || 'N/A',
    email: details.email || 'N/A',
    phoneNumber: details.phoneNumber,
    vendorBankCheck: details.vendorBankCheck,
    entity: details.entity || 'N/A',
    site: details.site || 'N/A',
    address: details.address || 'N/A',
    city: details.city || 'N/A',
    state: details.state || 'N/A',
    postcode: details.postcode || 'N/A',
    country: details.country || 'Australia',
    lastVerifiedDate: details.lastVerifiedDate,
    paymentTerms: details.paymentTerms,
  }
}

export function formatAmount(amount: number, currency: string = 'AUD'): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(
    amount,
  )
}
